import { useState, useCallback, useMemo, useEffect } from 'react';
import { Notification } from '../../../components/ui/custom-notification';
import type { TeamMember, TeamMemberDocument } from '../types/team';
import type { TeamMemberFilters } from '../components/dashboard/team/TeamMemberFilters';
import { useTeamMembersRedux } from '../../../redux/TeamMembers/hooks/useTeamMembersRedux';

export const useTeamMembersReduxHook = (availableCertifications?: Array<{label: string, value: string}>, projectId?: string | number) => {
  const {
    teamMembers,
    loading,
    error,
    deleteLoading,
    addLoading,
    updateLoading,
    projectRoles,
    certifications,
    rolesLoading,
    certificationsLoading,
    users,
    usersLoading,
    presignedUrl,
    presignedUrlLoading,
    uploadLoading,
    createUpdateLoading,
    isRefreshingData,
    deleteCertLoading,
    fetchTeamMembers: fetchTeamMembersRedux,
    deleteTeamMember: deleteTeamMemberRedux,
    addTeamMember: addTeamMemberRedux,
    updateTeamMember: updateTeamMemberRedux,
    fetchProjectRoles: fetchProjectRolesRedux,
    fetchCertifications: fetchCertificationsRedux,
    fetchUsers: fetchUsersRedux,
    getPresignedUrl: getPresignedUrlRedux,
    uploadCertificate: uploadCertificateRedux,
    createUpdateTeamMember: createUpdateTeamMemberRedux,
    clearTeamMembersError: clearTeamMembersErrorRedux,
    deleteTeamMemberCert: deleteTeamMemberCertRedux,
    createUpdateProjectLevelRoles: createUpdateProjectLevelRolesRedux,
    createUpdateRolesLoading,
    deleteProjectLevelRole: deleteProjectLevelRoleRedux,
    deleteRoleLoading
  } = useTeamMembersRedux();

  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember>({
    id: '',
    name: '',
    email: '',
    phone: '',
    role: '',
    certifications: [],
    qualifications: [],
    documents: [],
    status: 'active'
  });
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  
  // Add filter state
  const [filters, setFilters] = useState<TeamMemberFilters>({
    search: '',
    role: [],
    status: [],
  });

  // Fetch team members from Redux
  const fetchTeamMembers = useCallback(async () => {
    if (projectId) {
      fetchTeamMembersRedux({ projectId });
    } else {
      fetchTeamMembersRedux();
    }
  }, [fetchTeamMembersRedux, projectId]);

  // Load team members on component mount
  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  // Filter logic
  const filteredTeamMembers = useMemo(() => {
    return teamMembers.filter((member: any) => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          member.name.toLowerCase().includes(searchTerm) ||
          member.email.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }

      // Role filter - if no roles selected, show all; if roles selected, must match
      if (filters.role && filters.role.length > 0) {
        if (!filters.role.includes(member.role)) {
          return false;
        }
      }

      // Status filter - if no statuses selected, show all; if statuses selected, must match
      if (filters.status && filters.status.length > 0) {
        if (!filters.status.includes(member.status)) {
          return false;
        }
      }

      return true;
    });
  }, [teamMembers, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.role.length > 0 || filters.status.length > 0;
  }, [filters]);

  const handleTeamMemberDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !selectedTeamMember) return;
    
    const newDocuments: TeamMemberDocument[] = Array.from(e.target.files).map(file => ({
      id: crypto.randomUUID(),
      fileName: file.name,
      type: 'other',
      uploadDate: new Date().toISOString()
    }));

    setSelectedTeamMember({
      ...selectedTeamMember,
      documents: [...(selectedTeamMember.documents || []), ...newDocuments]
    });
  };

  const handleSaveTeamMember = async () => {
    // This function is no longer used for saving - the modal handles it directly
    // It's kept for backward compatibility but doesn't make API calls
    return;
  };

  const handleEditTeamMember = (member: TeamMember) => {
    // Map API certificates to frontend certifications based on document_tags
    let mappedCertifications: any[] = [];
    
    if (member.apiData?.certificates && member.apiData.certificates.length > 0) {
      // Extract unique document tags from API certificates
      const documentTags = member.apiData.certificates
        .flatMap((cert: any) => cert.document_tags || [])
        .filter((tag: string, index: number, arr: string[]) => arr.indexOf(tag) === index); // Remove duplicates
      
      // Map document tags to certification objects by searching through available options
      mappedCertifications = documentTags.map((tag: string) => {
        // Search for matching certification by name (case-insensitive)
        const matchingCert = availableCertifications?.find((cert: {label: string, value: string}) => 
          cert.label.toLowerCase().includes(tag.toLowerCase()) ||
          tag.toLowerCase().includes(cert.label.toLowerCase()) ||
          cert.value.toLowerCase() === tag.toLowerCase()
        );
        
        if (matchingCert) {
          return matchingCert;
        } else {
          // Create a new certification object if no match found
          return {
            label: tag,
            value: tag
          };
        }
      });
    }
    
    // Create updated member with mapped certifications
    const updatedMember = {
      ...member,
      certifications: mappedCertifications
    };
    
    setSelectedTeamMember(updatedMember);
    setIsAddingTeamMember(true);
  };

  const handleViewTeamMemberDocuments = (member: TeamMember) => {
    setSelectedTeamMember(member);
    // Additional logic for viewing documents can be added here
  };

  const handleAddNewTeamMember = () => {
    setSelectedTeamMember({
      id: '',
      name: '',
      role: '',
      email: '',
      phone: '',
      certifications: [],
      qualifications: [],
      documents: [],
      status: 'active',
      apiData: undefined
    });
    setIsAddingTeamMember(true);
  };

  const handleDeleteTeamMember = async (member: TeamMember) => {
    if (!member.team_member_id) {
      Notification.error('Cannot delete team member without API ID');
      return;
    }

    try {
      deleteTeamMemberRedux({ 
        teamMemberId: member.team_member_id,
        projectId: projectId || member.project_id 
      });
    } catch (err) {
      Notification.error('Failed to delete team member');
    }
  };

  return {
    teamMembers,
    filteredTeamMembers,
    selectedTeamMember,
    isAddingTeamMember,
    filters,
    hasActiveFilters,
    loading,
    error,
    deleteLoading,
    addLoading,
    updateLoading,
    projectRoles,
    certifications,
    rolesLoading,
    certificationsLoading,
    users,
    usersLoading,
    presignedUrl,
    presignedUrlLoading,
    uploadLoading,
    createUpdateLoading,
    isRefreshingData,
    deleteCertLoading,
    setSelectedTeamMember,
    setIsAddingTeamMember,
    setFilters,
    handleTeamMemberDocumentUpload,
    handleSaveTeamMember,
    handleEditTeamMember,
    handleViewTeamMemberDocuments,
    handleAddNewTeamMember,
    handleDeleteTeamMember,
    fetchTeamMembers,
    fetchProjectRoles: fetchProjectRolesRedux,
    fetchCertifications: fetchCertificationsRedux,
    fetchUsers: fetchUsersRedux,
    getPresignedUrl: getPresignedUrlRedux,
    uploadCertificate: uploadCertificateRedux,
    createUpdateTeamMember: createUpdateTeamMemberRedux,
    deleteTeamMember: deleteTeamMemberRedux,
    clearTeamMembersError: clearTeamMembersErrorRedux,
    deleteTeamMemberCert: deleteTeamMemberCertRedux,
    createUpdateProjectLevelRoles: createUpdateProjectLevelRolesRedux,
    createUpdateRolesLoading,
    deleteProjectLevelRole: deleteProjectLevelRoleRedux,
    deleteRoleLoading
  };
}; 